import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, IconButton, Button, Tooltip, Slider, Popover, CircularProgress } from "@mui/material";
import {
  Close as CloseIcon,
  Undo as UndoIcon,
  Crop as CropIcon,
  Create as PenIcon,
  BorderColor as HighlighterIcon,
  BlurOn as MosaicIcon,
  EmojiEmotions as StickerIcon,
  RotateLeft as RotateLeftIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

// WhatsApp-style photo editor: crop / rotate, pen, highlighter, mosaic (pixelate) and emoji
// stickers, with undo. Everything is kept as a list of operations replayed on the original
// photo, so undo works across crops too. Strokes and crops are in the coordinates of the image
// as it was when they were made (each crop starts a new coordinate space). Stickers stay
// movable until a crop (which bakes them in) or Done.

const MAX_DIM = 2560; // bigger photos are scaled down on load — WhatsApp re-compresses anyway
const COLORS = ["#ffffff", "#000000", "#ff3b30", "#ff9500", "#ffcc00", "#34c759", "#007aff", "#af52de"];
const STICKERS = [
  "😀", "😂", "😍", "🥰", "😎", "🤔", "😮", "😢", "😡", "🥳", "👍", "👎", "👏", "🙏", "💪", "👌",
  "✌️", "👉", "👈", "👆", "👇", "❤️", "🔥", "⭐", "✅", "❌", "⚠️", "❓", "❗", "💯", "🎉", "🎁",
  "📦", "🚚", "✈️", "🏠", "📍", "💰", "💵", "🧾", "📷", "📱", "⏰", "📅", "🔒", "🆗", "🔴", "🟢",
];
const EMOJI_FONT = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
const BRUSH = { pen: 6, highlight: 24, mosaic: 32 }; // default widths in SCREEN px
const HANDLE = 14; // sticker / crop handle radius in screen px

const newCanvas = (w, h) => {
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
};

const copyCanvas = (src) => {
  const c = newCanvas(src.width, src.height);
  c.getContext("2d").drawImage(src, 0, 0);
  return c;
};

// Pixelated copy: drawn tiny, then scaled back up without smoothing.
const pixelate = (src) => {
  const block = Math.max(8, Math.round(Math.max(src.width, src.height) / 60));
  const small = newCanvas(Math.ceil(src.width / block), Math.ceil(src.height / block));
  small.getContext("2d").drawImage(src, 0, 0, small.width, small.height);
  const out = newCanvas(src.width, src.height);
  const ctx = out.getContext("2d");
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(small, 0, 0, out.width, out.height);
  return out;
};

// Mosaic paints with the pixelated image as the stroke's pattern — the pattern sits at the
// canvas origin, so it lines up exactly with the photo underneath.
const drawStroke = (ctx, op, mosaicSource) => {
  if (!op.points.length) return;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = op.width;
  if (op.mode === "mosaic") {
    ctx.strokeStyle = ctx.createPattern(mosaicSource, "no-repeat");
  } else {
    ctx.strokeStyle = op.color;
    if (op.mode === "highlight") ctx.globalAlpha = 0.4;
  }
  ctx.beginPath();
  const [x0, y0] = op.points[0];
  ctx.moveTo(x0, y0);
  if (op.points.length === 1) ctx.lineTo(x0 + 0.01, y0); // a tap still leaves a dot
  for (let i = 1; i < op.points.length; i++) ctx.lineTo(op.points[i][0], op.points[i][1]);
  ctx.stroke();
  ctx.restore();
};

const drawSticker = (ctx, s) => {
  ctx.save();
  ctx.translate(s.x, s.y);
  ctx.rotate(s.rot);
  ctx.font = `${s.size}px ${EMOJI_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(s.emoji, 0, 0);
  ctx.restore();
};

const withStickers = (layer, stickers) => {
  if (!stickers.length) return layer;
  const c = copyCanvas(layer);
  const ctx = c.getContext("2d");
  stickers.forEach((s) => drawSticker(ctx, s));
  return c;
};

const rotateCanvas = (src, deg) => {
  const d = ((deg % 360) + 360) % 360;
  if (!d) return src;
  const swap = d === 90 || d === 270;
  const c = newCanvas(swap ? src.height : src.width, swap ? src.width : src.height);
  const ctx = c.getContext("2d");
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((d * Math.PI) / 180);
  ctx.drawImage(src, -src.width / 2, -src.height / 2);
  return c;
};

const cropCanvas = (src, r) => {
  const c = newCanvas(r.w, r.h);
  c.getContext("2d").drawImage(src, r.x, r.y, r.w, r.h, 0, 0, c.width, c.height);
  return c;
};

const applyOp = (layer, op) => {
  if (op.t === "stroke") {
    const out = copyCanvas(layer);
    drawStroke(out.getContext("2d"), op, op.mode === "mosaic" ? pixelate(layer) : null);
    return out;
  }
  if (op.t === "crop") return cropCanvas(rotateCanvas(withStickers(layer, op.stickers), op.rotate), op.rect);
  return layer;
};

// Sticker geometry in image px: half-size of its box, and where its two handles sit.
const stickerHalf = (s) => s.size * 0.62;
const rotatePoint = (x, y, a) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
const stickerHandles = (s) => {
  const h = stickerHalf(s);
  const [sx, sy] = rotatePoint(h, h, s.rot);
  const [dx, dy] = rotatePoint(-h, -h, s.rot);
  return { scale: [s.x + sx, s.y + sy], remove: [s.x + dx, s.y + dy] };
};
const hitSticker = (s, x, y) => {
  const [lx, ly] = rotatePoint(x - s.x, y - s.y, -s.rot);
  const h = stickerHalf(s);
  return Math.abs(lx) <= h && Math.abs(ly) <= h;
};

let stickerSeq = 0;

const PhotoEditor = ({ file, onCancel, onDone, t }) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const originalRef = useRef(null); // canvas of the photo as loaded
  const layerRef = useRef(null); // original + all committed ops
  const layerOpsRef = useRef([]); // the ops layerRef was built from
  const liveRef = useRef(null); // stroke being drawn { op, mosaic }
  const dragRef = useRef(null); // sticker / crop drag in progress

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState({ ops: [], stickers: [] });
  const [history, setHistory] = useState([]); // earlier docs, for undo
  const [tool, setTool] = useState("pen"); // pen | highlight | mosaic | crop
  const [color, setColor] = useState(COLORS[2]);
  const [brush, setBrush] = useState(BRUSH);
  const [selected, setSelected] = useState(null); // sticker id
  const [stickerAnchor, setStickerAnchor] = useState(null);
  const [crop, setCrop] = useState(null); // { rotate, rect, source } while cropping
  const [display, setDisplay] = useState({ w: 0, h: 0 });
  const [, force] = useState(0);
  const redraw = useCallback(() => force((n) => n + 1), []);

  // ── Load the photo (EXIF orientation applied by createImageBitmap) ──
  useEffect(() => {
    let alive = true;
    setLoading(true);
    createImageBitmap(file)
      .then((bmp) => {
        if (!alive) return;
        const scale = Math.min(1, MAX_DIM / Math.max(bmp.width, bmp.height));
        const c = newCanvas(bmp.width * scale, bmp.height * scale);
        c.getContext("2d").drawImage(bmp, 0, 0, c.width, c.height);
        originalRef.current = c;
        layerRef.current = c;
        layerOpsRef.current = [];
        setDoc({ ops: [], stickers: [] });
        setHistory([]);
        setLoading(false);
      })
      .catch(() => alive && onCancel());
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // ── Keep the committed layer in step with doc.ops (incremental when one op was added) ──
  const layer = (() => {
    if (!originalRef.current) return null;
    const built = layerOpsRef.current;
    const ops = doc.ops;
    if (built === ops) return layerRef.current;
    let base;
    let from;
    if (ops.length === built.length + 1 && built.every((op, i) => op === ops[i])) {
      base = layerRef.current;
      from = built.length;
    } else {
      base = originalRef.current;
      from = 0;
    }
    for (let i = from; i < ops.length; i++) base = applyOp(base, ops[i]);
    layerRef.current = base;
    layerOpsRef.current = ops;
    return base;
  })();

  // History lives in refs (mirrored to state for the Undo button) so pushing an entry never
  // happens inside another state updater — StrictMode runs updaters twice.
  const docRef = useRef(doc);
  docRef.current = doc;
  const historyRef = useRef(history);
  historyRef.current = history;
  const pushHistory = useCallback((entry) => {
    historyRef.current = [...historyRef.current.slice(-29), entry];
    setHistory(historyRef.current);
  }, []);
  const commit = useCallback(
    (next) => {
      const cur = docRef.current;
      const value = typeof next === "function" ? next(cur) : next;
      pushHistory(cur);
      docRef.current = value;
      setDoc(value);
    },
    [pushHistory]
  );

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (!h.length) return;
    const prev = h[h.length - 1];
    historyRef.current = h.slice(0, -1);
    setHistory(historyRef.current);
    docRef.current = prev;
    setDoc(prev);
    setSelected(null);
  }, []);

  // ── Fit the canvas into the available area ──
  const source = crop ? crop.source : layer; // what the canvas shows
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !source) return undefined;
    const fit = () => {
      const s = Math.min(wrap.clientWidth / source.width, wrap.clientHeight / source.height);
      setDisplay({ w: Math.floor(source.width * s), h: Math.floor(source.height * s) });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [source]);

  const pxPerScreen = source && display.w ? source.width / display.w : 1; // image px per screen px

  // ── Paint ──
  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !source) return;
    if (canvas.width !== source.width) canvas.width = source.width;
    if (canvas.height !== source.height) canvas.height = source.height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0);
    const k = pxPerScreen;

    if (crop) {
      const r = crop.rect;
      ctx.save();
      ctx.fillStyle = "rgba(0,0,0,.55)";
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.rect(r.x, r.y, r.w, r.h);
      ctx.fill("evenodd");
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2 * k;
      ctx.strokeRect(r.x, r.y, r.w, r.h);
      // rule-of-thirds guides
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1 * k;
      for (let i = 1; i < 3; i++) {
        ctx.beginPath();
        ctx.moveTo(r.x + (r.w * i) / 3, r.y);
        ctx.lineTo(r.x + (r.w * i) / 3, r.y + r.h);
        ctx.moveTo(r.x, r.y + (r.h * i) / 3);
        ctx.lineTo(r.x + r.w, r.y + (r.h * i) / 3);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      ctx.fillStyle = "#fff";
      [
        [r.x, r.y],
        [r.x + r.w, r.y],
        [r.x, r.y + r.h],
        [r.x + r.w, r.y + r.h],
      ].forEach(([x, y]) => {
        ctx.beginPath();
        ctx.arc(x, y, 7 * k, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();
      return;
    }

    const live = liveRef.current;
    if (live) drawStroke(ctx, live.op, live.mosaic);
    doc.stickers.forEach((s) => drawSticker(ctx, s));
    const sel = doc.stickers.find((s) => s.id === selected);
    if (sel) {
      const h = stickerHalf(sel);
      ctx.save();
      ctx.translate(sel.x, sel.y);
      ctx.rotate(sel.rot);
      ctx.setLineDash([6 * k, 4 * k]);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 1.5 * k;
      ctx.strokeRect(-h, -h, h * 2, h * 2);
      ctx.restore();
      const { scale, remove } = stickerHandles(sel);
      ctx.save();
      ctx.fillStyle = "#fff";
      ctx.beginPath();
      ctx.arc(scale[0], scale[1], HANDLE * 0.7 * k, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#e53935";
      ctx.beginPath();
      ctx.arc(remove[0], remove[1], HANDLE * 0.7 * k, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2 * k;
      const d = HANDLE * 0.3 * k;
      ctx.beginPath();
      ctx.moveTo(remove[0] - d, remove[1] - d);
      ctx.lineTo(remove[0] + d, remove[1] + d);
      ctx.moveTo(remove[0] + d, remove[1] - d);
      ctx.lineTo(remove[0] - d, remove[1] + d);
      ctx.stroke();
      ctx.restore();
    }
  }, [source, crop, doc.stickers, selected, pxPerScreen]);

  useEffect(() => {
    paint();
  });

  // ── Pointer input ──
  const toImage = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return [(e.clientX - rect.left) * (source.width / rect.width), (e.clientY - rect.top) * (source.height / rect.height)];
  };
  const near = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by) <= HANDLE * pxPerScreen;

  const onPointerDown = (e) => {
    if (!source || saving) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const [x, y] = toImage(e);

    if (crop) {
      const r = crop.rect;
      const corners = { nw: [r.x, r.y], ne: [r.x + r.w, r.y], sw: [r.x, r.y + r.h], se: [r.x + r.w, r.y + r.h] };
      const corner = Object.keys(corners).find((c) => near(x, y, ...corners[c]));
      if (corner) dragRef.current = { kind: "crop-corner", corner, start: [x, y], rect: { ...r } };
      else if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) dragRef.current = { kind: "crop-move", start: [x, y], rect: { ...r } };
      return;
    }

    // Stickers first: handles of the selected one, then any sticker under the pointer.
    const sel = doc.stickers.find((s) => s.id === selected);
    if (sel) {
      const { scale, remove } = stickerHandles(sel);
      if (near(x, y, ...remove)) {
        commit((cur) => ({ ...cur, stickers: cur.stickers.filter((s) => s.id !== sel.id) }));
        setSelected(null);
        return;
      }
      if (near(x, y, ...scale)) {
        dragRef.current = { kind: "sticker-scale", id: sel.id, start: { ...sel }, moved: false };
        return;
      }
    }
    const hit = [...doc.stickers].reverse().find((s) => hitSticker(s, x, y));
    if (hit) {
      setSelected(hit.id);
      dragRef.current = { kind: "sticker-move", id: hit.id, start: [x, y], orig: { ...hit }, moved: false };
      return;
    }
    if (selected) {
      setSelected(null);
      return;
    }

    const op = {
      t: "stroke",
      mode: tool,
      color,
      width: brush[tool] * pxPerScreen,
      points: [[x, y]],
    };
    liveRef.current = { op, mosaic: tool === "mosaic" ? pixelate(layer) : null };
    redraw();
  };

  const onPointerMove = (e) => {
    if (!source) return;
    const drag = dragRef.current;
    const live = liveRef.current;
    if (!drag && !live) return;
    const [x, y] = toImage(e);

    if (live) {
      live.op.points.push([x, y]);
      paint();
      return;
    }
    if (drag.kind === "crop-move" || drag.kind === "crop-corner") {
      const minSide = 40 * pxPerScreen;
      const W = source.width;
      const H = source.height;
      const dx = x - drag.start[0];
      const dy = y - drag.start[1];
      let { x: rx, y: ry, w: rw, h: rh } = drag.rect;
      if (drag.kind === "crop-move") {
        rx = Math.min(Math.max(0, rx + dx), W - rw);
        ry = Math.min(Math.max(0, ry + dy), H - rh);
      } else {
        let x1 = rx;
        let y1 = ry;
        let x2 = rx + rw;
        let y2 = ry + rh;
        if (drag.corner.includes("w")) x1 = Math.min(Math.max(0, x1 + dx), x2 - minSide);
        if (drag.corner.includes("e")) x2 = Math.max(Math.min(W, x2 + dx), x1 + minSide);
        if (drag.corner.includes("n")) y1 = Math.min(Math.max(0, y1 + dy), y2 - minSide);
        if (drag.corner.includes("s")) y2 = Math.max(Math.min(H, y2 + dy), y1 + minSide);
        rx = x1;
        ry = y1;
        rw = x2 - x1;
        rh = y2 - y1;
      }
      setCrop((c) => ({ ...c, rect: { x: rx, y: ry, w: rw, h: rh } }));
      return;
    }
    // Sticker drags change the doc without a history entry each frame — one entry is added
    // when the drag ends (see onPointerUp).
    if (drag.kind === "sticker-move") {
      drag.moved = true;
      const nx = drag.orig.x + x - drag.start[0];
      const ny = drag.orig.y + y - drag.start[1];
      setDoc((cur) => ({ ...cur, stickers: cur.stickers.map((s) => (s.id === drag.id ? { ...s, x: nx, y: ny } : s)) }));
    } else if (drag.kind === "sticker-scale") {
      drag.moved = true;
      const s0 = drag.start;
      const d0 = Math.hypot(stickerHalf(s0), stickerHalf(s0));
      const d1 = Math.max(10, Math.hypot(x - s0.x, y - s0.y));
      const a0 = Math.PI / 4; // the handle sits on the box's diagonal
      const rot = Math.atan2(y - s0.y, x - s0.x) - a0;
      const size = Math.max(16, s0.size * (d1 / d0));
      setDoc((cur) => ({ ...cur, stickers: cur.stickers.map((s) => (s.id === drag.id ? { ...s, size, rot } : s)) }));
    }
  };

  const onPointerUp = () => {
    const live = liveRef.current;
    const drag = dragRef.current;
    liveRef.current = null;
    dragRef.current = null;
    if (live) {
      commit((cur) => ({ ...cur, ops: [...cur.ops, live.op] }));
      return;
    }
    if (drag && (drag.kind === "sticker-move" || drag.kind === "sticker-scale") && drag.moved) {
      // Record the pre-drag doc so undo puts the sticker back.
      const cur = docRef.current;
      pushHistory({
        ...cur,
        stickers: cur.stickers.map((s) => (s.id === drag.id ? { ...(drag.orig || drag.start) } : s)),
      });
    }
  };

  // ── Stickers ──
  const addSticker = (emoji) => {
    setStickerAnchor(null);
    if (!layer) return;
    const id = `st${++stickerSeq}`;
    const size = Math.round(Math.min(layer.width, layer.height) / 5);
    commit((cur) => ({
      ...cur,
      stickers: [...cur.stickers, { id, emoji, x: layer.width / 2, y: layer.height / 2, size, rot: 0 }],
    }));
    setSelected(id);
  };

  // ── Crop ──
  const startCrop = () => {
    if (!layer) return;
    setSelected(null);
    const src = withStickers(layer, doc.stickers);
    setCrop({ rotate: 0, base: src, source: src, rect: { x: 0, y: 0, w: src.width, h: src.height } });
  };
  const rotateCrop = () => {
    setCrop((c) => {
      const rotate = (c.rotate + 270) % 360; // 90° to the left each tap
      const source = rotateCanvas(c.base, rotate);
      return { ...c, rotate, source, rect: { x: 0, y: 0, w: source.width, h: source.height } };
    });
  };
  const resetCrop = () => setCrop((c) => ({ ...c, rotate: 0, source: c.base, rect: { x: 0, y: 0, w: c.base.width, h: c.base.height } }));
  const applyCrop = () => {
    const c = crop;
    setCrop(null);
    const r = c.rect;
    const full = !c.rotate && r.x === 0 && r.y === 0 && r.w === c.source.width && r.h === c.source.height;
    if (full) return; // nothing changed
    const rect = { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.w), h: Math.round(r.h) };
    commit((cur) => ({ ops: [...cur.ops, { t: "crop", rotate: c.rotate, rect, stickers: cur.stickers }], stickers: [] }));
  };

  // ── Done ──
  const finish = () => {
    if (!layer || saving) return;
    setSaving(true);
    setSelected(null);
    const out = newCanvas(layer.width, layer.height);
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#fff"; // JPEG has no transparency
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(withStickers(layer, doc.stickers), 0, 0);
    out.toBlob(
      (blob) => {
        setSaving(false);
        if (!blob) return;
        const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
        onDone(new File([blob], name, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92
    );
  };

  // Ctrl+Z undo, Delete removes the selected sticker.
  useEffect(() => {
    const onKey = (e) => {
      if (crop) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
      } else if ((e.key === "Delete" || e.key === "Backspace") && selected) {
        e.preventDefault();
        commit((cur) => ({ ...cur, stickers: cur.stickers.filter((s) => s.id !== selected) }));
        setSelected(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [crop, selected, undo, commit]);

  const toolBtn = (key, icon, label, onClick) => (
    <Tooltip title={label} key={key}>
      <IconButton
        onClick={onClick}
        aria-label={label}
        sx={{
          color: "#fff",
          bgcolor: tool === key && !crop ? "rgba(255,255,255,.22)" : "transparent",
          "&:hover": { bgcolor: "rgba(255,255,255,.15)" },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  const drawing = tool === "pen" || tool === "highlight" || tool === "mosaic";

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: (th) => th.zIndex.modal + 2, bgcolor: "#0c0a14", display: "flex", flexDirection: "column" }}>
      {/* Top bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 1 }}>
        {crop ? (
          <>
            <IconButton onClick={() => setCrop(null)} aria-label={t("common.cancel")} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1 }} />
            <Tooltip title={t("whatsappChat.editor.rotate")}>
              <IconButton onClick={rotateCrop} aria-label={t("whatsappChat.editor.rotate")} sx={{ color: "#fff" }}>
                <RotateLeftIcon />
              </IconButton>
            </Tooltip>
            <Button variant="text" color="inherit" onClick={resetCrop} sx={{ color: "#fff" }}>
              {t("whatsappChat.editor.reset")}
            </Button>
            <Button variant="contained" color="secondary" startIcon={<CheckIcon />} onClick={applyCrop}>
              {t("whatsappChat.editor.apply")}
            </Button>
          </>
        ) : (
          <>
            <IconButton onClick={onCancel} disabled={saving} aria-label={t("common.cancel")} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1 }} />
            {toolBtn("crop", <CropIcon />, t("whatsappChat.editor.crop"), startCrop)}
            <Tooltip title={t("whatsappChat.editor.sticker")}>
              <IconButton onClick={(e) => setStickerAnchor(e.currentTarget)} aria-label={t("whatsappChat.editor.sticker")} sx={{ color: "#fff" }}>
                <StickerIcon />
              </IconButton>
            </Tooltip>
            {toolBtn("pen", <PenIcon />, t("whatsappChat.editor.pen"), () => setTool("pen"))}
            {toolBtn("highlight", <HighlighterIcon />, t("whatsappChat.editor.highlighter"), () => setTool("highlight"))}
            {toolBtn("mosaic", <MosaicIcon />, t("whatsappChat.editor.mosaic"), () => setTool("mosaic"))}
            <Tooltip title={t("whatsappChat.editor.undo")}>
              <span>
                <IconButton onClick={undo} disabled={!history.length || saving} aria-label={t("whatsappChat.editor.undo")} sx={{ color: "#fff", "&.Mui-disabled": { color: "rgba(255,255,255,.3)" } }}>
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color="secondary"
              onClick={finish}
              disabled={loading || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            >
              {t("whatsappChat.editor.done")}
            </Button>
          </>
        )}
      </Box>

      {/* Colour + size for the drawing tools */}
      {!crop && drawing && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, px: 2, pb: 1, flexWrap: "wrap" }}>
          {tool !== "mosaic" &&
            COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => setColor(c)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: c,
                  cursor: "pointer",
                  border: color === c ? "3px solid #fff" : "2px solid rgba(255,255,255,.4)",
                  boxShadow: color === c ? "0 0 0 2px rgba(0,0,0,.5)" : "none",
                }}
              />
            ))}
          <Slider
            size="small"
            color="secondary"
            min={tool === "pen" ? 2 : 10}
            max={tool === "pen" ? 30 : 80}
            value={brush[tool]}
            onChange={(_, v) => setBrush((b) => ({ ...b, [tool]: v }))}
            aria-label={t("whatsappChat.editor.size")}
            sx={{ width: 160, ml: 2 }}
          />
        </Box>
      )}

      {/* Canvas */}
      <Box ref={wrapRef} sx={{ flex: 1, minHeight: 0, mx: 2, mb: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {loading ? (
          <CircularProgress color="secondary" />
        ) : (
          <Box
            component="canvas"
            ref={canvasRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            sx={{
              width: display.w,
              height: display.h,
              touchAction: "none",
              cursor: crop ? "move" : drawing ? "crosshair" : "default",
              display: "block",
            }}
          />
        )}
      </Box>

      <Popover
        open={!!stickerAnchor}
        anchorEl={stickerAnchor}
        onClose={() => setStickerAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: (th) => th.zIndex.modal + 3 }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(8, 40px)", gap: 0.5, p: 1 }}>
          {STICKERS.map((s) => (
            <Box
              key={s}
              onClick={() => addSticker(s)}
              sx={{ fontSize: 26, lineHeight: "40px", textAlign: "center", cursor: "pointer", borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }}
            >
              {s}
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default PhotoEditor;
